import * as React from 'react';
import { capitalize } from 'lodash';
import { connect } from 'react-redux';

import DeliverableMediaUploadPlaceholder from 'modules/deliverables/components/media/parts/placeholder/DeliverableMediaUploadPlaceholder';
import DeliverableMediaAttachments from 'modules/deliverables/components/media/parts/DeliverableMediaAttachments';
import AudienceScreenshotFeedbackModal from 'modules/influencers/components/audience-screenshot-feedback-modal/AudienceScreenshotFeedbackModal';
import ImageUploader, { MEDIA_TYPES_IMAGES } from 'components/ImageUploader';
import { FormField } from 'components/form';

import { checkAnalyticsScreenshots } from 'modules/influencers/reducers/influencerAnalyticScreenshots';

import { API, post } from 'utils/api';
import { __t, applyTranslations } from 'utils/language';
import { equals, isMultipleItemsInArrayRequired } from 'utils/validators';
import { ASSET_TYPES } from 'utils/influencers';
import { logError } from 'utils/log';
import { CHANNEL_TYPE_INSTAGRAM } from 'utils/channels';

import type { ChannelAnalyticsScreenShotData } from 'types';

import './channel-audience-data-image.scss';

applyTranslations(require('./channel-audience-data-image.i18.json'));

type ChannelAudienceDataImageState = {
  mode: 'create' | 'uploading' | 'drop',
  isUploadingFailed: boolean
};

type OwnProps = {
  channel: {
    type: string,
    assets?: Array<ChannelAnalyticsScreenShotData>
  },
  isAdmin?: boolean,
  isCreationStep?: boolean,
  hasConnectedFacebookOverride?: boolean,
  onAudienceDataImageChange: (a: Array<ChannelAnalyticsScreenShotData>) => void
};

type DispatchProps = {
  dispatchCheckAnalyticsScreenshots: (
    files: Array<{url: string}>,
    feedbackModal: React.ReactNode,
    channelType: string
  ) => Promise<Array<ChannelAnalyticsScreenShotData>>
};

type Props = OwnProps & DispatchProps;

const { IMAGES } = API.END_POINTS.API;

const ChannelAudienceDataImage = (props: Props) => {
  const {
    channel,
    isAdmin,
    isCreationStep,
    hasConnectedFacebookOverride,
    onAudienceDataImageChange,
    dispatchCheckAnalyticsScreenshots
  } = props;

  const assets = channel.assets || [];
  const [state, setState] = React.useState<ChannelAudienceDataImageState>({
    mode: 'create',
    isUploadingFailed: false
  });

  const getTempId = (assetType: string, index: number) => {
    const datetime = (new Date()).getTime();

    return `temp-${datetime}-${index}-${assetType}`;
  };

  const uploadImage = (image, index) => {
    const url = IMAGES;
    const data = new FormData();

    data.append('file', image);

    return post(url, { data })
      .then((response) => ({
        id: getTempId(ASSET_TYPES[channel.type], index),
        mediaType: 'image',
        assetType: ASSET_TYPES[channel.type],
        url: response.data.url
      }));
  };

  const uploadImages = (images: Array<File>) => {
    let isUploadingFailed = false;

    setState({
      ...state,
      mode: 'uploading',
      isUploadingFailed
    });

    return Promise.all(images.map(uploadImage))
      .then(uploadedImages => dispatchCheckAnalyticsScreenshots(uploadedImages, (
        <AudienceScreenshotFeedbackModal channel={channel.type} />
      ), channel.type))
      .then((uploadedImages) => {
        onAudienceDataImageChange([...assets, ...uploadedImages] as Array<ChannelAnalyticsScreenShotData>);
      })
      .catch((error) => {
        logError(error);

        isUploadingFailed = true;
      })
      .finally(() => {
        setState({
          ...state,
          mode: 'create',
          isUploadingFailed
        });
      });
  };

  const handleDeleteImage = (item) => {
    const modifiedAssets = assets.filter(asset => asset.url !== item.url);

    onAudienceDataImageChange(modifiedAssets);
  };

  const shouldUseEqualsValidator = isCreationStep
    && channel.type === CHANNEL_TYPE_INSTAGRAM
    && !hasConnectedFacebookOverride
    && assets.length < 2;

  const specificValidator = shouldUseEqualsValidator
    ? equals(String(hasConnectedFacebookOverride), String(true), __t('channel_audience_data_image.no_facebook_or_analytics_error'))
    : isMultipleItemsInArrayRequired(assets, __t('channel_audience_data_image.required_field_with_two_ss'));

  const validators = !isAdmin
    ? [specificValidator]
    : [];

  return (
    <section className="channel-audience-data__container">
      <FormField
        direction="column"
        showValidationIcon={false}
        showRequiredMark
        labelType="highlighted"
        fontSize="small"
        label={__t('channel_audience_data_image.label', { channelType: capitalize(channel.type) })}
        validators={validators}
        immediateValidationError={state.isUploadingFailed ? __t('channel_audience_data_image.upload_failed_message') : ''}
      >
        <DeliverableMediaAttachments
          canUpload
          placeholderForUpload={(
            <ImageUploader
              key={`uploader-${state.mode}`}
              multiple
              mediaTypes={MEDIA_TYPES_IMAGES}
              onUpload={(files) => {
                uploadImages([...files]);
              }}
            >
              <DeliverableMediaUploadPlaceholder
                size="normal"
                mode={state.mode}
                tooltip={__t('channel_audience_data_image.upload_image_placeholder')}
              />
            </ImageUploader>
          )}
          attachments={assets}
          onDeleteAttachment={handleDeleteImage}
        />
      </FormField>
    </section>
  );
};

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchCheckAnalyticsScreenshots: (files: Array<{url: string}>, feedbackModal: React.ReactNode, channelType: string) =>
    dispatch(checkAnalyticsScreenshots(files, feedbackModal, channelType))
});

export default connect(null, mapDispatchToProps)(ChannelAudienceDataImage);
